import {
  OpportunitiesListData,
  opportunitiesListInfiniteOptions,
  opportunitiesLogsListQueryKey,
  opportunitiesMergePartialUpdateMutation,
  opportunitiesRetrieveOptions,
  opportunitiesRetrieveQueryKey,
  PaginatedReturnOpportunityListReadable,
  ReturnOpportunityReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { getCustomFields } from '@crm/utils/getCustomFields'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors, transformOptions } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Col, Modal, notification, Row, Skeleton } from 'antd'
import { ModalProps } from 'antd/lib'
import {
  ArrowRight,
  Call,
  HierarchySquare3,
  ProfileCircle,
  Sms
} from 'iconsax-react'

import { MouseEvent, useEffect, useState } from 'react'

import { useParams } from 'react-router-dom'

import { InfoLeadCard } from './InfoLeadCard'

export const MergeOpportunityModal = (props: ModalProps) => {
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<number>()

  const { t, currentLang } = useLocales()
  const { opportunityId } = useParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { data: currentOpportunity } = useQuery({
    ...opportunitiesRetrieveOptions({
      path: { id: Number(opportunityId) }
    }),
    enabled: !!opportunityId
  })

  const { data: selectedOpportunity, isLoading: isLoadingOpportunity } =
    useQuery({
      ...opportunitiesRetrieveOptions({
        path: { id: Number(selectedOpportunityId) }
      }),
      enabled: !!opportunityId && !!selectedOpportunityId
    })

  useEffect(() => {
    if (!props.open) {
      setSelectedOpportunityId(undefined)
    }
  }, [props.open])

  const mutationConfig = {
    onSuccess: () => {
      invalidateQueries([
        opportunitiesRetrieveQueryKey({
          path: { id: Number(opportunityId) }
        }),
        opportunitiesLogsListQueryKey({
          path: { opportunity_id: String(opportunityId) }
        })
      ])

      props.onCancel?.({} as MouseEvent<HTMLButtonElement>)

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    },
    onError: (e: unknown) => showApiErrors(e)
  }

  const { mutate: mergeOpportunity, isPending: isPendingOpportunity } =
    useMutation({
      ...opportunitiesMergePartialUpdateMutation(),
      ...mutationConfig
    })

  const onSubmit = () => {
    if (selectedOpportunityId) {
      mergeOpportunity({
        path: { id: Number(opportunityId) },
        body: { opportunity_to_merge: selectedOpportunityId }
      })
    }
  }

  const onChange = (id: number) => {
    if (opportunityId) {
      setSelectedOpportunityId(id)
    } else {
      setSelectedOpportunityId(undefined)
    }
  }

  const mergeOpportunityCustomFields = getCustomFields(
    selectedOpportunity?.custom_field_values,
    t,
    currentLang
  )

  const currentOpportunityCustomFields = getCustomFields(
    currentOpportunity?.custom_field_values,
    t,
    currentLang
  )

  return (
    <Modal
      title={t('activities.merge_opportunity')}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      onOk={onSubmit}
      centered
      width={700}
      okButtonProps={{
        loading: isPendingOpportunity,
        disabled: !selectedOpportunityId
      }}
      {...props}
    >
      <InfiniteSelect<
        OpportunitiesListData,
        PaginatedReturnOpportunityListReadable,
        ReturnOpportunityReadable
      >
        value={selectedOpportunityId}
        popupMatchSelectWidth
        getQueryFunction={opportunitiesListInfiniteOptions}
        placeholder={t('main.select')}
        getOptions={({ options }) =>
          transformOptions({
            options: options?.filter(
              (option) => option?.id !== Number(opportunityId)
            ),
            label: 'name',
            value: 'id'
          })
        }
        loading={isLoadingOpportunity}
        baseOptions={{
          query: {
            preference: selectedOpportunityId
              ? [Number(selectedOpportunityId)]
              : undefined
          }
        }}
        onChange={(value) => onChange(value)}
        className="w-full"
      />

      {isLoadingOpportunity && <Skeleton paragraph={{ rows: 4 }} />}

      {selectedOpportunity && selectedOpportunityId && (
        <>
          <Row
            gutter={24}
            align="middle"
            justify="space-between"
            className="mt-20"
          >
            <Col span={10}>
              <InfoLeadCard
                loading={isLoadingOpportunity}
                leadDetails={[
                  {
                    icon: <ProfileCircle size={16} />,
                    label: selectedOpportunity?.name || ''
                  },
                  {
                    icon: <Sms size={16} />,
                    label: selectedOpportunity?.contact?.email || ''
                  },
                  {
                    icon: <Call size={16} />,
                    label:
                      selectedOpportunity?.contact?.phone_numbers
                        ?.find(({ is_primary }) => is_primary)
                        ?.number?.toString() || '-'
                  },
                  ...(mergeOpportunityCustomFields ?? []).map((field) => ({
                    icon: <HierarchySquare3 size={16} />,
                    label: field.label
                  }))
                ]}
              />
            </Col>

            <ArrowRight size={36} />

            <Col span={10}>
              <InfoLeadCard
                leadDetails={[
                  {
                    icon: <ProfileCircle size={20} />,
                    label: currentOpportunity?.name || ''
                  },
                  {
                    icon: <Sms size={20} />,
                    label: currentOpportunity?.contact?.email || '-'
                  },
                  {
                    icon: <Call size={20} />,
                    label:
                      currentOpportunity?.contact?.phone_numbers
                        ?.find(({ is_primary }) => is_primary)
                        ?.number?.toString() || '-'
                  },
                  ...(currentOpportunityCustomFields ?? []).map((field) => ({
                    icon: <HierarchySquare3 size={16} />,
                    label: field.label
                  }))
                ]}
              />
            </Col>
          </Row>

          <Row className="mt-20">
            <Col span={24} style={{ textAlign: 'center' }}>
              <Text type="danger" strong>
                {t('activities.missing_data_replacement', {
                  leadName: currentOpportunity?.name,
                  dataName: selectedOpportunity?.name
                })}
              </Text>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  )
}
